import { Theme } from '@radix-ui/themes';
import { useTenantContext } from '@trashlab/sitesapp/src/organizations/piedmont/contexts/use-tenant-context';
import { BaseSEO } from '@trashlab/sitesapp/src/organizations/piedmont/components/seo/base-seo';
import { OrganizationSchema } from '@trashlab/sitesapp/src/organizations/piedmont/components/seo/schemas/organization-schema';
import { Footer } from '@trashlab/sitesapp/src/organizations/piedmont/template/components/shared/footer-section';
import { Nav } from '@trashlab/sitesapp/src/organizations/piedmont/template/components/shared/nav';
import { useVariantValue } from '@trashlab/sitesapp/src/organizations/piedmont/lib/utils/get-variant-value';
import { GoogleMapsLoader } from '@trashlab/sitesapp/src/organizations/piedmont/components/shared/google-maps-loader';
import AnimationWrapper from '@trashlab/sitesapp/src/organizations/piedmont/components/shared/animation-wrapper';
import { PortalContainerProvider } from '@trashlab/sitesapp/src/organizations/piedmont/contexts/portal-container-context';
import { asAddress } from '@trashlab/api/src/library/server/utils/json-compatibility';
import { SEOChecker } from '@trashlab/sitesapp/src/organizations/piedmont/components/dev/seo-checker';

export const DefaultTemplate = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { website } = useTenantContext();

  const primaryColor = useVariantValue('THEME_PRIMARY_COLOR', '#3463F8');
  const secondaryColor = useVariantValue('THEME_SECONDARY_COLOR', '#5460C3');
  const backgroundColor = useVariantValue('THEME_BACKGROUND_COLOR', '#001B5E');
  const backgroundColorLight = useVariantValue(
    'THEME_BACKGROUND_COLOR_LIGHT',
    '#EFF3FE',
  );

  const seoTitle = useVariantValue('SEO_TITLE', 'Dumpster Rental Services | Same Day Delivery & Competitive Rates');
  const seoDescription = useVariantValue('SEO_DESCRIPTION', 'Professional dumpster rental services with same-day delivery. Competitive rates, flexible rental periods, and expert waste management for residential and commercial projects.');
  const seoKeywords = useVariantValue('SEO_KEYWORDS', [
    { value: 'dumpster rental', orderIndex: 0 },
    { value: 'same day dumpster delivery', orderIndex: 1 },
    { value: 'construction debris removal', orderIndex: 2 },
    { value: 'residential dumpster rental', orderIndex: 3 },
    { value: 'commercial dumpster rental', orderIndex: 4 },
    { value: 'roll off dumpster', orderIndex: 5 },
    { value: 'waste removal services', orderIndex: 6 },
    { value: 'junk removal', orderIndex: 7 },
    { value: 'demolition debris removal', orderIndex: 8 },
    { value: 'affordable dumpster rental', orderIndex: 9 },
    { value: 'construction waste disposal', orderIndex: 10 },
    { value: 'garbage removal', orderIndex: 11 },
    { value: 'trash removal', orderIndex: 12 },
    { value: 'recycling services', orderIndex: 13 },
    { value: 'waste management', orderIndex: 14 }
  ]);

  // Get organization contact info for Organization schema
  const facebookUrl = useVariantValue('FACEBOOK_LINK', '');
  const twitterUrl = useVariantValue('X_LINK', '');
  const logoUrl = useVariantValue<string>('LOGO_IMAGE', '');

  // Get organization address for local SEO
  const orgAddress = asAddress(website?.organization?.address);
  const city = orgAddress?.city;
  const state = orgAddress?.state;

  // Build social links array (only using valid variant keys)
  const socialLinks = [facebookUrl, twitterUrl].filter(Boolean);

  // Geo location for enhanced local SEO
  const geoLocation = city && state ? {
    city,
    region: state,
    country: 'US',
  } : undefined;

  return (
    <Theme
      radius="medium"
      style={
        {
          '--primary-color': primaryColor,
          '--secondary-color': secondaryColor,
          '--background-color': backgroundColor,
          '--background-color-light': backgroundColorLight,
          '--success-color': '#40C057',
          '--danger-color': '#FA5252',
          '--font-family': 'system-ui',
          overflowX: 'hidden',
          width: '100%',
        } as React.CSSProperties
      }
    >
      <PortalContainerProvider>
        <AnimationWrapper>
          <GoogleMapsLoader />

          <BaseSEO
            title={seoTitle}
            description={seoDescription}
            keywords={
              Array.isArray(seoKeywords) ? seoKeywords.map((k) => k.value) : []
            }
            name={website?.organization?.name}
            canonicalUrl={window.location.href}
            themeColor={primaryColor}
            geoLocation={geoLocation}
          />
          {/* Enhanced Organization schema for local business SEO */}
          <OrganizationSchema
            name={website?.organization?.name || 'Dumpster Rental Services'}
            description={seoDescription}
            url={typeof window !== 'undefined' ? window.location.origin : ''}
            logo={
              logoUrl
                ? logoUrl.startsWith('http')
                  ? logoUrl
                  : `${typeof window !== 'undefined' ? window.location.origin : ''}${logoUrl}`
                : undefined
            }
            address={city && state ? {
              addressLocality: city,
              addressRegion: state,
              addressCountry: 'US',
            } : undefined}
            sameAs={socialLinks}
            priceRange="$$"
            paymentAccepted={['Visa', 'Mastercard', 'American Express', 'Apple Pay', 'Google Pay', 'PayPal']}
            currenciesAccepted="USD"
          />
          <Nav />
          <main>{children}</main>
          <Footer />
          {/* SEO Checker - only visible in non-production environments */}
          <SEOChecker />
        </AnimationWrapper>
      </PortalContainerProvider>
    </Theme>
  );
};
